import React from "react";
import axios from "../../shared/axios.utils";
import { DASH_API } from "../../shared/url.consts";
import { useLendingFlags } from "../../shared/feature-flags/feature-flags";
import { sendAppInsightsServerError } from "../../shared/insights/app-insights.utils";
import { AppInsightsErrorType } from "../../shared/insights/app-insights.consts";
import { useGetWisrProfile } from "../../redux/wisr-profile/wisr-profile.hooks";

declare global {
  interface Window {
    cw?: (...args: unknown[]) => void;
  }
}

const WIDGET_SCRIPT_ID = "ai-chat-widget-script";
let isWidgetLoadStarted = false;

export const _resetWidgetLoadStarted = () => {
  isWidgetLoadStarted = false;
};

export const _getWidgetLoadStarted = () => isWidgetLoadStarted;

export const ChatWidgetComponent: React.FC = () => {
  const { financialAssitanceBotWebEnabled } = useLendingFlags<{
    financialAssitanceBotWebEnabled: boolean;
  }>();
  const wisrProfile = useGetWisrProfile();

  React.useEffect(() => {
    if (
      isWidgetLoadStarted ||
      !wisrProfile ||
      document.getElementById(WIDGET_SCRIPT_ID)
    ) {
      return;
    }
    const profile = wisrProfile;
    isWidgetLoadStarted = true;

    const cw = window.cw;
    const widgetUrl = process.env.GATSBY_AI_CHAT_WIDGET_SCRIPT_URL;
    const widgetEnv = process.env.GATSBY_AI_CHAT_WIDGET_ENVIRONMENT;

    if (!widgetUrl) {
      console.error("[ChatWidget] missing GATSBY_AI_CHAT_WIDGET_SCRIPT_URL");
      isWidgetLoadStarted = false;
      return;
    }

    const loadWidget = (token: string | null) => {
      if (!cw) {
        return;
      }
      cw("init", widgetEnv);
      cw("setTheme", {
        launcherBg: "#15384F",
        headerBg: "#7AC2BB",
        panelBg: "#fff",
        textColor: "#000",
      });
      if (token) {
        cw("token", token);
      }
      const script = document.createElement("script");
      script.id = WIDGET_SCRIPT_ID;
      script.src = widgetUrl;
      script.async = true;
      document.body.appendChild(script);
      const customerFirstName = profile.firstName ?? "";
      const customerSurname = profile.lastName ?? "";
      try {
        cw("setCustomer", {
          wisrCustomerId: profile.wisrCustomerId,
          customerFirstName,
          customerSurname,
          email: profile.email,
          phone: profile.mobile,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : "unknown";
        sendAppInsightsServerError(
          `setCustomer failed with: ${message}`,
          AppInsightsErrorType.CHAT_WIDGET_SET_CUSTOMER
        );
      }
    };

    const attemptTokenExchange = (
      retriesLeft: number
    ): Promise<string | null> =>
      axios
        .post(`${DASH_API}/LoanApplication/aichatbot_token_exchange`)
        .then((response) => response.data.accessToken as string | null)
        .catch((error: unknown) => {
          if (retriesLeft > 0) {
            return attemptTokenExchange(retriesLeft - 1);
          }
          const status =
            error instanceof Error && "response" in error
              ? (error as Error & { response?: { status?: number } }).response
                  ?.status ?? "unknown"
              : "unknown";
          sendAppInsightsServerError(
            `Token exchange failed with status: ${status}`,
            AppInsightsErrorType.CHAT_WIDGET_TOKEN_EXCHANGE
          );
          return null;
        });

    attemptTokenExchange(3).then(loadWidget);
  }, [wisrProfile]);

  React.useEffect(() => {
    const applyDisplay = (el: HTMLElement) => {
      el.style.display = financialAssitanceBotWebEnabled ? "block" : "none";
    };

    const existingEl = document.getElementById("cw-shadow-host");
    if (existingEl) {
      applyDisplay(existingEl);
      return;
    }

    // widget.js loads asynchronously — watch for #cw-shadow-host to appear
    /* istanbul ignore next */
    if (typeof MutationObserver === "undefined") {
      return;
    }

    const observer = new MutationObserver(() => {
      const el = document.getElementById("cw-shadow-host");
      if (el) {
        applyDisplay(el);
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [financialAssitanceBotWebEnabled]);

  return null;
};
